import React, { useEffect, useState, useRef, memo, useCallback } from 'react';
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  StatusBar,
  Image,
  Text,
  PanResponder,
  Animated,
  Platform,
} from 'react-native';
import VideoPlayer from 'react-native-video';
import { theme } from '../constants/theme';
import { getSupabaseFileUrl } from '../services/imageService';
import { X, Play, Pause } from '../../assets/icons';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

interface FullScreenMediaProps {
  visible: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  title?: string;
}

const FullScreenMedia: React.FC<FullScreenMediaProps> = memo(({
  visible,
  onClose,
  mediaUrl,
  mediaType,
  title,
}) => {
  const [videoPaused, setVideoPaused] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showMiniControls, setShowMiniControls] = useState(false);
  const miniControlsTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Video ref for web HTML5 video element
  const videoRef = useRef<HTMLVideoElement | null>(null);
  
  // Animated values for smooth zoom and pan
  const scale = useRef(new Animated.Value(1)).current;
  const translateX = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(0)).current;
  
  // Current values for calculations
  const currentScale = useRef(1);
  const currentTranslateX = useRef(0);
  const currentTranslateY = useRef(0);
  
  // Pan responder state
  const panResponderRef = useRef({
    lastPanX: 0,
    lastPanY: 0,
    lastScale: 1,
    lastDistance: undefined as number | undefined,
  }).current;

  const toggleVideoPlayback = useCallback(() => {
    setVideoPaused(!videoPaused);
  }, [videoPaused]);

  const toggleControls = useCallback(() => {
    setShowControls(!showControls);
  }, [showControls]);

  const handleClose = useCallback(() => {
    setVideoPaused(true);
    setShowControls(true);
    resetZoom();
    onClose();
  }, [onClose]);

  const resetZoom = useCallback(() => {
    Animated.parallel([
      Animated.timing(scale, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(translateX, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      currentScale.current = 1;
      currentTranslateX.current = 0;
      currentTranslateY.current = 0;
      panResponderRef.lastPanX = 0;
      panResponderRef.lastPanY = 0;
      panResponderRef.lastScale = 1;
      setIsZoomed(false);
    });
  }, []);

  const zoomIn = useCallback(() => {
    const newScale = Math.min(currentScale.current * 1.5, 4);
    animateToScale(newScale);
  }, []);

  const zoomOut = useCallback(() => {
    const newScale = Math.max(currentScale.current / 1.5, 0.5);
    animateToScale(newScale);
  }, []);

  const animateToScale = (newScale: number) => {
    currentScale.current = newScale;
    setIsZoomed(newScale > 1);
    
    Animated.timing(scale, {
      toValue: newScale,
      duration: 300,
      useNativeDriver: true,
    }).start();
  };

  const lastTapRef = useRef(0);
  const handleImageTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 250) {
      // Double tap: toggle zoom in/out
      if (currentScale.current > 1) {
        resetZoom();
      } else {
        animateToScale(2);
      }
    } else {
      // Single tap: show mini controls briefly
      setShowMiniControls(true);
      if (miniControlsTimerRef.current) clearTimeout(miniControlsTimerRef.current);
      miniControlsTimerRef.current = setTimeout(() => setShowMiniControls(false), 2000);
    }
    lastTapRef.current = now;
  }, []);

  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => currentScale.current > 1,
    onMoveShouldSetPanResponder: (evt, gestureState) => {
      // Enable pan when zoomed in
      if (currentScale.current > 1) return true;
      
      // Enable pinch-to-zoom
      if (evt.nativeEvent.touches.length === 2) return true;
      
      return false;
    },
    onPanResponderGrant: () => {
      panResponderRef.lastPanX = currentTranslateX.current;
      panResponderRef.lastPanY = currentTranslateY.current;
    },
    onPanResponderMove: (evt, gestureState) => {
      const { touches } = evt.nativeEvent;
      
      // Handle pinch-to-zoom
      if (touches.length === 2) {
        const touch1 = touches[0];
        const touch2 = touches[1];
        
        const distance = Math.sqrt(
          Math.pow(touch2.pageX - touch1.pageX, 2) + 
          Math.pow(touch2.pageY - touch1.pageY, 2)
        );
        
        if (panResponderRef.lastDistance) {
          const scaleChange = distance / panResponderRef.lastDistance;
          const newScale = Math.max(0.5, Math.min(4, currentScale.current * scaleChange));
          
          if (Math.abs(newScale - currentScale.current) > 0.01) {
            currentScale.current = newScale;
            scale.setValue(newScale);
            setIsZoomed(newScale > 1);
          }
        }
        
        panResponderRef.lastDistance = distance;
      }
      // Handle pan when zoomed
      else if (currentScale.current > 1) {
        const newTranslateX = panResponderRef.lastPanX + gestureState.dx;
        const newTranslateY = panResponderRef.lastPanY + gestureState.dy;
        
        // Calculate maximum translation based on image size and scale
        const imageWidth = screenWidth;
        const imageHeight = screenHeight * 0.8;
        const scaledWidth = imageWidth * currentScale.current;
        const scaledHeight = imageHeight * currentScale.current;
        
        const maxTranslateX = Math.max(0, (scaledWidth - imageWidth) / 2);
        const maxTranslateY = Math.max(0, (scaledHeight - imageHeight) / 2);
        
        const clampedX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newTranslateX));
        const clampedY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newTranslateY));
        
        currentTranslateX.current = clampedX;
        currentTranslateY.current = clampedY;
        
        translateX.setValue(clampedX);
        translateY.setValue(clampedY);
      }
    },
    onPanResponderRelease: () => {
      panResponderRef.lastPanX = currentTranslateX.current;
      panResponderRef.lastPanY = currentTranslateY.current;
      panResponderRef.lastDistance = undefined;
    },
  });

  useEffect(() => {
    if (visible && mediaType === 'video') {
      setVideoPaused(false);
    } else {
      setVideoPaused(true);
    }
  }, [visible, mediaType]);

  // Handle video playback for web
  useEffect(() => {
    if (Platform.OS === 'web' && mediaType === 'video' && videoRef.current) {
      if (videoPaused) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
        });
      }
    }
  }, [videoPaused, mediaType]);

  // Load video when modal opens
  useEffect(() => {
    if (Platform.OS === 'web' && visible && mediaType === 'video' && videoRef.current) {
      videoRef.current.load();
    }
  }, [visible, mediaType]);

  const renderMedia = () => {
    // Debug log
    if (!mediaUrl || mediaUrl.trim() === '') {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Invalid media URL</Text>
        </View>
      );
    }

    const fullUrl = getSupabaseFileUrl(mediaUrl);
    if (!fullUrl || (typeof fullUrl === 'object' && 'uri' in fullUrl && !fullUrl.uri)) {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Could not generate media URL</Text>
        </View>
      );
    }

    if (mediaType === 'video') {
      // Use HTML5 video for web, react-native-video for native
      if (Platform.OS === 'web') {
        const videoUrl = typeof fullUrl === 'object' && 'uri' in fullUrl ? fullUrl.uri : String(fullUrl);
        
        // Use React.createElement to create HTML video element for web
        const VideoElement = React.createElement('video', {
          ref: videoRef,
          src: videoUrl,
          style: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'black',
          },
          controls: true,
          playsInline: true,
          onLoadedData: () => {
            if (!videoPaused && videoRef.current) {
              videoRef.current.play().catch(err => {
                console.error('Error auto-playing video:', err);
              });
            }
          },
          onError: (e: any) => {
            console.error('Video playback error:', e);
          },
        });
        
        return (
          <View style={styles.videoContainer}>
            {showControls && (
              <View style={styles.videoHeader}>
                <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
                  <X size={24} color="white" />
                </TouchableOpacity>
                {title && <Text style={styles.title}>{title}</Text>}
              </View>
            )}
            {VideoElement}
            {showControls && (
              <TouchableOpacity 
                style={styles.videoPlayButton} 
                onPress={toggleVideoPlayback} 
                activeOpacity={0.7}
              >
                <Text style={{ color: 'white', fontSize: 18 }}>{videoPaused ? '▶' : '⏸'}</Text>
              </TouchableOpacity>
            )}
          </View>
        );
      } else {
        // Native video player
        return (
          <View style={styles.videoContainer}>
            {showControls && (
              <View style={styles.videoHeader}>
                <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
                  <X size={24} color="white" />
                </TouchableOpacity>
                {title && <Text style={styles.title}>{title}</Text>}
              </View>
            )}
            <VideoPlayer
              source={typeof fullUrl === 'object' && 'uri' in fullUrl ? fullUrl : { uri: String(fullUrl) }}
              style={styles.video}
              paused={videoPaused}
              resizeMode="contain"
              controls
              onLoad={() => setVideoPaused(false)}
              ignoreSilentSwitch="ignore"
            />
            {showControls && (
              <TouchableOpacity style={styles.videoPlayButton} onPress={toggleVideoPlayback} activeOpacity={0.7}>
                <Text style={{ color: 'white', fontSize: 18 }}>{videoPaused ? 'Play' : 'Pause'}</Text>
              </TouchableOpacity>
            )}
          </View>
        );
      }
    } else {
      // Use enhanced zoom implementation
      return (
        <View style={styles.imageContainer}>
          {showControls && (
            <View style={styles.imageHeader}>
              <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
                <X size={24} color="white" />
              </TouchableOpacity>
              {title && <Text style={styles.title}>{title}</Text>}
            </View>
          )}
          <View style={styles.gestureContainer} {...panResponder.panHandlers}>
            <TouchableOpacity 
              style={styles.imageTouchable}
              onPress={handleImageTap}
              activeOpacity={1}
            >
              <Animated.Image
                source={typeof fullUrl === 'object' && 'uri' in fullUrl ? fullUrl : { uri: String(fullUrl) }}
                style={[
                  styles.zoomableImage,
                  {
                    transform: [
                      { translateX: translateX },
                      { translateY: translateY },
                      { scale: scale },
                    ],
                  },
                ]}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>
          {/* Gesture hint removed to avoid overlaying content */}
          {showMiniControls && (
            <View style={styles.miniControls} pointerEvents="box-none">
              <TouchableOpacity style={styles.miniButton} onPress={zoomOut}>
                <Text style={styles.miniButtonText}>-</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.miniButton} onPress={resetZoom}>
                <Text style={styles.miniButtonText}>⟳</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.miniButton} onPress={zoomIn}>
                <Text style={styles.miniButtonText}>+</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      );
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={false}
      animationType="fade"
      onRequestClose={handleClose}
    >
      <StatusBar hidden />
      <View style={styles.container}>
        {/* Media Content */}
        <TouchableOpacity
          style={styles.mediaContainer}
          onPress={mediaType === 'video' ? toggleControls : undefined}
          activeOpacity={1}
        >
          {renderMedia()}
        </TouchableOpacity>

        {/* Footer Info */}
        {showControls && mediaType === 'video' && (
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              Tap to {videoPaused ? 'play' : 'pause'} • Double tap to toggle controls
            </Text>
          </View>
        )}
      </View>
    </Modal>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'black',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingHorizontal: 20,
    paddingBottom: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  title: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
  },
  mediaContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  media: {
    width: screenWidth,
    height: screenHeight * 0.8,
  },
  videoContainer: {
    width: screenWidth,
    height: screenHeight * 0.8,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  videoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingHorizontal: 20,
    paddingBottom: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  video: {
    width: screenWidth,
    height: screenHeight * 0.8,
  },
  videoPlayButton: {
    position: 'absolute',
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footer: {
    paddingHorizontal: 20,
    paddingBottom: 30,
    alignItems: 'center',
  },
  footerText: {
    color: 'white',
    fontSize: 14,
    opacity: 0.8,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
  },
  errorText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  imageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingHorizontal: 20,
    paddingBottom: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  imageContainer: {
    flex: 1,
    backgroundColor: 'black',
  },
  zoomableImage: {
    width: screenWidth,
    height: screenHeight * 0.8,
  },
  gestureContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageTouchable: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resetButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    marginLeft: 10,
  },
  resetButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  zoomControls: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 10,
  },
  zoomButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 5,
  },
  zoomButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  zoomLevelText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    marginHorizontal: 10,
    minWidth: 50,
    textAlign: 'center',
  },
  zoomHint: {
    position: 'absolute',
    bottom: 50,
    left: 20,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 20,
    alignItems: 'center',
  },
  zoomHintText: {
    color: 'white',
    fontSize: 12,
    textAlign: 'center',
  },
  miniControls: {
    position: 'absolute',
    bottom: 30,
    right: 20,
    flexDirection: 'row',
    gap: 10,
    backgroundColor: 'rgba(0,0,0,0.35)',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 20,
    alignItems: 'center',
  },
  miniButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  miniButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '700',
  },
});

export default FullScreenMedia; 